<template>
  <lb-drawer
    :value="value"
    heading="Notify"
    maxwidth="850"
    data-testid="notify-modal"
    @input="$emit('input', $event)"
  >
    <template v-slot:body>
      <div class="notify-modal__form" data-testid="notify-modal__form">
        <div class="d-flex align-center dataCount gap-120 mb-6">
          <h6 class="font-weight-medium count-label">Selected</h6>
          <h3 class="font-weight-medium">{{ selectedItems.length }}</h3>
        </div>

        <div class="notify-modal__field mb-4">
          <label class="notify-modal__label font-weight-bold mb-1 d-block">To</label>
          <v-combobox
            v-model="toEmails"
            :items="emailDropdownItems"
            multiple
            chips
            small-chips
            deletable-chips
            outlined
            dense
            placeholder="Enter email addresses"
            :hide-details="!toError"
            :error-messages="toError"
            data-testid="notify-modal__to-input"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip small close @click:close="removeToEmail(index)" :key="'to-' + index">
                {{ item }}
              </v-chip>
            </template>
          </v-combobox>
        </div>

        <div class="notify-modal__field mb-4">
          <label class="notify-modal__label font-weight-bold mb-1 d-block">CC</label>
          <v-combobox
            v-model="ccEmails"
            :items="emailDropdownItems"
            multiple
            chips
            small-chips
            deletable-chips
            outlined
            dense
            placeholder="Enter CC email addresses"
            :hide-details="!ccError"
            :error-messages="ccError"
            data-testid="notify-modal__cc-input"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip small close @click:close="removeCcEmail(index)" :key="'cc-' + index">
                {{ item }}
              </v-chip>
            </template>
          </v-combobox>
        </div>

        <div class="notify-modal__field mb-4">
          <label class="notify-modal__label font-weight-bold mb-1 d-block">Subject*</label>
          <lb-string
            v-model="subject"
            label=""
            :error="subjectError"
            data-testid="notify-modal__subject"
            @input="subjectError = ''"
          />
        </div>

        <div class="notify-modal__field">
          <label class="notify-modal__label font-weight-bold mb-1 d-block">Email Body</label>
          <v-textarea
            v-model="emailBody"
            outlined
            rows="14"
            auto-grow
            hide-details="auto"
            placeholder="Enter email body"
            data-testid="notify-modal__body-input"
          />
        </div>
      </div>
    </template>

    <template v-slot:actions>
      <v-chip class="noTransform ru-info-chip gap-2 align-center">
        <span><v-icon>mdi-information-outline</v-icon> Recipients will be saved for future suggestions.</span>
      </v-chip>
      <v-spacer />
      <base-button
        label="Cancel"
        secondary
        data-testid="notify-modal__cancel-btn"
        @click="handleCancel"
      />
      <base-button
        label="Send Email"
        primary
        image="/img/icons/SendIConWhite.svg"
        class="ml-2 EmailBtn"
        :loading="loading"
        :disabled="!isFormValid"
        data-testid="notify-modal__send-btn"
        @click="handleSend"
      />
    </template>
  </lb-drawer>
</template>

<script>
import './NotifyModal.scss';

const DEFAULT_EMAIL_BODY = `Hello,

As part of the ongoing review/close process, please find below the latest status of the information requests. A detailed summary of the selected requests has been attached for your reference.

Action Required:
- Kindly review the pending items highlighted in the attached file
- Provide the required information/documents at the earliest against each of the request
- If any items have already been addressed, please disregard or share an update accordingly in the relevant request
- If you have any questions, need clarification, or require additional context on any of the requests, please feel free to reach out.

We appreciate your timely support in helping us complete this process efficiently.

Best regards,`;

export default {
  name: 'NotifyModal',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    suggestedContacts: {
      type: Array,
      default: () => [],
    },
    infoOwnerList: {
      type: Array,
      default: () => [],
    },
    emailTemplate: {
      type: Object,
      default: () => ({ subject: '', body: '', placeholders: [] }),
    },
    selectedItems: {
      type: Array,
      default: () => [],
    },
    projectName: {
      type: String,
      default: '',
    },
    userName: {
      type: String,
      default: '',
    },
    userEmail: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      toEmails: [],
      ccEmails: [],
      subject: '',
      emailBody: DEFAULT_EMAIL_BODY,
      toError: '',
      ccError: '',
      subjectError: '',
    };
  },
  computed: {
    emailDropdownItems() {
      const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
      const suggested = (this.suggestedContacts || []).filter((v) => v && isEmail(v));
      const ownerEmails = (this.infoOwnerList || [])
        .map((owner) => owner.email || '')
        .filter((v) => v && isEmail(v));
      return [...new Set([...suggested, ...ownerEmails])];
    },
    isFormValid() {
      return this.toEmails.length > 0
        && this.subject.trim().length > 0
        && this.emailBody.trim().length > 0;
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.resetForm();
      }
    },
    toEmails(val) {
      if (val && val.length > 0) this.toError = '';
    },
    ccEmails(val) {
      if (val && val.length > 0) this.ccError = '';
    },
  },
  methods: {
    replacePlaceholders(text) {
      if (!text) return text;
      const today = this.$nova?.formatDate
        ? this.$nova.formatDate(new Date())
        : new Date().toLocaleDateString();
      return text
        .replace(/\{\{project_name\}\}/gi, this.projectName || '')
        .replace(/\{\{current_date\}\}/gi, today)
        .replace(/\{\{sender_name\}\}/gi, this.userName || '')
        .replace(/\{\{sender_email\}\}/gi, this.userEmail || '')
        .replace(/\{\{request_count\}\}/gi, String(this.selectedItems.length || 0));
    },
    resetForm() {
      this.toEmails = [];
      this.ccEmails = [];
      const rawSubject = (this.emailTemplate && this.emailTemplate.subject) || '';
      const rawBody = (this.emailTemplate && this.emailTemplate.body) || DEFAULT_EMAIL_BODY;
      this.subject = this.replacePlaceholders(rawSubject);
      this.emailBody = this.replacePlaceholders(rawBody);
      this.toError = '';
      this.ccError = '';
      this.subjectError = '';
    },
    removeToEmail(index) {
      this.toEmails.splice(index, 1);
    },
    removeCcEmail(index) {
      this.ccEmails.splice(index, 1);
    },
    handleCancel() {
      this.$emit('input', false);
    },
    handleSend() {
      const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
      if (this.toEmails.length === 0) {
        this.toError = 'At least one recipient is required';
        return;
      }
      const invalidTo = this.toEmails.filter((e) => !isEmail(e));
      if (invalidTo.length > 0) {
        this.toError = `Invalid email address: ${invalidTo[0]}`;
        return;
      }
      const invalidCc = this.ccEmails.filter((e) => !isEmail(e));
      if (invalidCc.length > 0) {
        this.ccError = `Invalid CC email address: ${invalidCc[0]}`;
        return;
      }
      if (this.subject.trim().length === 0) {
        this.subjectError = 'Subject is required';
        return;
      }
      this.toError = '';
      this.ccError = '';
      this.subjectError = '';
      if (!this.isFormValid) return;
      this.$emit('send', {
        to: [...this.toEmails],
        cc: [...this.ccEmails],
        subject: this.subject,
        body: this.emailBody,
        selectedItems: this.selectedItems,
      });
    },
  },
};
</script>
